<template>
  <div
    v-if="visiblePages.length"
    class="wide-bg-EXPONDO-secondary-light pb-2 lg:py-2"
  >
    <div
      class="flex flex-col lg:flex-row items-center gap-4"
    >
      <div class="lg:hidden w-full wide-bg-EXPONDO-primary py-2">
        <div class="flex justify-between items-center gap-4">
          <SimpleCarouselButton
            v-if="shouldShowButtons"
            :arrow-type="SIMPLE_CAROUSEL_ARROW.prev"
            @arrow-clicked="togglePage"
          />
          <p class="text-xl font-bold text-EXPONDO-white">
            {{ currentTitle }}
          </p>
          <SimpleCarouselButton
            v-if="shouldShowButtons"
            :arrow-type="SIMPLE_CAROUSEL_ARROW.next"
            @arrow-clicked="togglePage"
          />
        </div>
      </div>
      <SimpleCarouselButton
        v-if="shouldShowButtons"
        :arrow-type="SIMPLE_CAROUSEL_ARROW.prev"
        class="max-lg:hidden"
        @arrow-clicked="togglePage"
      />
      <p class="max-lg:hidden text-xl font-bold text-EXPONDO-primary flex-grow">
        {{ currentTitle }}
      </p>
      <div class="grid grid-cols-2 lg:flex gap-4 max-w-full w-full lg:max-w-3xl lg:w-auto">
        <UspBlock
          v-for="block in currentBlocks"
          :key="block.key"
          :icon-path="block.iconPath"
          :title="block.title"
        />
      </div>
      <SimpleCarouselButton
        v-if="shouldShowButtons"
        :arrow-type="SIMPLE_CAROUSEL_ARROW.next"
        class="max-lg:hidden"
        @arrow-clicked="togglePage"
      />
    </div>
  </div>
</template>

<script lang="ts">
import SimpleCarouselButton from '~/components/molecules/SimpleCarousel/SimpleCarouselButton.vue';
import UspBlock from '~/components/molecules/UspBlock/UspBlock.vue';
import { filterUspBlocks } from '~~/helpers/cms/filterUspBlocks';
import type { UspBlock as UspBlockType } from '~~/types/cms/UspBlock';
import type { SimpleCarouselArrow } from '~~/types/components/SimpleCarousel/SimpleCarouselArrows';
import { SIMPLE_CAROUSEL_ARROW } from '~~/types/components/SimpleCarousel/SimpleCarouselArrows';

type UspCarouselPageType = {
  title: string;
  blocks: UspBlockType[];
};

export default defineComponent({
  name: 'UspCarousel',
  components: {
    UspBlock,
    SimpleCarouselButton,
  },
  props: {
    pages: {
      type: Array as PropType<UspCarouselPageType[]>,
      required: true,
      validator: (pages: UspCarouselPageType[]) => {

        return pages.every((page: UspCarouselPageType) => {

          return page.title && page.blocks;

        });

      },
    },
  },
  setup (props) {

    const currentPage = ref(0);
    const { isExtraGuaranteeEnabled } = useStoreVsfExtended();
    const uspFeatureToggles = computed(() => ({
      isExtraGuaranteeEnabled: isExtraGuaranteeEnabled.value,
    }));
    const visiblePages = computed(() => props.pages
      .map(page => ({
        ...page,
        blocks: filterUspBlocks(page.blocks, uspFeatureToggles.value),
      }))
      .filter(page => page.blocks.length),
    );
    const activePage = computed(() => {

      if (!visiblePages.value.length) {

        return undefined;

      }

      return visiblePages.value[currentPage.value % visiblePages.value.length];

    });

    const togglePage = (arrowType: SimpleCarouselArrow) => {

      if (!visiblePages.value.length) {

        return;

      }

      const increment = arrowType === SIMPLE_CAROUSEL_ARROW.prev ? -1 : 1;
      currentPage.value = (currentPage.value + increment + visiblePages.value.length) % visiblePages.value.length;

    };

    const currentTitle = computed(() => {

      return activePage.value?.title || '';

    });
    const currentBlocks = computed(() => {

      return activePage.value?.blocks || [];

    });
    const shouldShowButtons = computed(() => visiblePages.value?.length > 1);

    return {
      currentPage,
      visiblePages,
      currentTitle,
      currentBlocks,
      SIMPLE_CAROUSEL_ARROW,
      togglePage,
      shouldShowButtons,
    };

  },
});
</script>
